/**
 * Recursively scans and sanitizes data objects for Firestore.
 * Firestore does not support nested arrays (arrays of arrays), e.g. [[1, 2], [3, 4]].
 * This function detects nested arrays and converts them into objects with stringified indices.
 * For example: [["a", "b"], ["c", "d"]] -> [{"0": "a", "1": "b"}, {"0": "c", "1": "d"}]
 * 
 * Also strips massive base64 payloads / dataURLs (e.g., uploaded document binary strings)
 * to strictly ensure Firestore documents never exceed the 1,048,576 byte (1MB) limit.
 */
export function sanitizeFirestoreData(data: any): any {
  if (data === null || data === undefined) {
    return data;
  }

  // Handle Date
  if (data instanceof Date) {
    return data;
  }

  // Handle String - prevent large base64/data-URI or huge strings from exceeding 1MB limit
  if (typeof data === 'string') {
    // If it's a data URL or base64 string longer than 1024 chars, replace with a clean reference
    if (data.startsWith('data:') && data.length > 512) {
      const mime = data.substring(5, data.indexOf(';')) || 'application/pdf';
      return `https://storage.procureflow.cloud/docs/blob-${Math.abs(data.length)}.${mime.includes('pdf') ? 'pdf' : 'bin'}`;
    }
    // If raw string is longer than 200,000 characters, truncate to prevent 1MB Firestore limit breach
    if (data.length > 200000) {
      return data.substring(0, 200000) + '... [Truncated for Firestore storage limit]';
    }
    return data;
  }

  // Handle Array
  if (Array.isArray(data)) {
    return data.map(item => {
      if (Array.isArray(item)) {
        // Nested array detected! Convert this nested array to an object: { "0": val, "1": val }
        const obj: Record<string, any> = {};
        item.forEach((val, i) => {
          obj[String(i)] = sanitizeFirestoreData(val);
        });
        return obj;
      }
      return sanitizeFirestoreData(item);
    });
  }

  // Handle Object
  if (typeof data === 'object') {
    const copy: any = {};
    for (const key of Object.keys(data)) {
      // Omit raw binary / base64 properties that might have been added to file objects
      if ((key === 'dataUrl' || key === 'base64' || key === 'rawBuffer') && typeof data[key] === 'string' && data[key].length > 512) {
        continue;
      }
      copy[key] = sanitizeFirestoreData(data[key]);
    }
    return copy;
  }

  return data;
}

/**
 * Ensures payload fits comfortably within Firestore's 1,048,576-byte per-document limit.
 * Leaves safety margin (max ~750,000 bytes).
 */
export function ensureFirestoreSafeSize<T>(data: T): T {
  const sanitized = sanitizeFirestoreData(data);
  try {
    const str = JSON.stringify(sanitized);
    // 750KB threshold
    if (str.length > 750000) {
      console.warn(`[Firestore Sanitizer] Payload size is ${str.length} bytes, performing defensive compaction.`);
      if (sanitized && typeof sanitized === 'object') {
        const mutable: any = { ...sanitized };
        if (mutable.description && typeof mutable.description === 'string' && mutable.description.length > 10000) {
          mutable.description = mutable.description.substring(0, 10000) + '...';
        }
        if (mutable.details?.response?.rows && Array.isArray(mutable.details.response.rows) && mutable.details.response.rows.length > 50) {
          mutable.details.response.rows = mutable.details.response.rows.slice(0, 50);
        }
        if (mutable.generatedContent && typeof mutable.generatedContent === 'string' && mutable.generatedContent.length > 50000) {
          mutable.generatedContent = mutable.generatedContent.substring(0, 50000) + '...';
        }
        return mutable;
      }
    }
  } catch (e) {
    // Return sanitized if JSON stringify fails
  }
  return sanitized;
}

/**
 * Firestore document IDs cannot contain slashes because slashes are used
 * as path segment separators. This function replaces all slashes with
 * underscores to ensure the document ID remains a single path segment.
 */
export function getSafeSolicitationDocId(id: string): string {
  if (!id) return id;
  return id.replace(/\//g, '_');
}

